import React from 'react';
import { LayerWrapper } from './LayerWrapper';
import type { TemplateConfig, TemplateFontStyle } from '../../types';

interface HeadlineLayerProps {
  config: TemplateConfig['headline'];
  text: string;
}

interface SubheadlineLayerProps {
  config: TemplateConfig['subheadline'];
  text: string;
}

/** Map textAlign to flexbox justify (horizontal) and align (vertical) values. */
function alignmentToFlex(textAlign: string | undefined): {
  justifyContent: React.CSSProperties['justifyContent'];
  alignItems: React.CSSProperties['alignItems'];
} {
  switch (textAlign) {
    case 'center': return { justifyContent: 'center', alignItems: 'center' };
    case 'right':  return { justifyContent: 'flex-end', alignItems: 'flex-start' };
    default:       return { justifyContent: 'flex-start', alignItems: 'flex-start' };
  }
}

export function HeadlineLayer({ config, text }: HeadlineLayerProps) {
  if (!text) return null;
  const { justifyContent, alignItems } = alignmentToFlex(config.font?.textAlign);

  return (
    <LayerWrapper position={config.position} animation={config.animation}>
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent,
          alignItems,
        }}
      >
        <span
          style={{
            ...fontToStyle(config.font),
            display: '-webkit-box',
            WebkitLineClamp: config.maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {text}
        </span>
      </div>
    </LayerWrapper>
  );
}

export function SubheadlineLayer({ config, text }: SubheadlineLayerProps) {
  if (!text) return null;
  const { justifyContent, alignItems } = alignmentToFlex(config.font?.textAlign);

  return (
    <LayerWrapper position={config.position} animation={config.animation}>
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent,
          alignItems,
        }}
      >
        <span
          style={{
            ...fontToStyle(config.font),
            display: '-webkit-box',
            WebkitLineClamp: config.maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {text}
        </span>
      </div>
    </LayerWrapper>
  );
}

function fontToStyle(font: TemplateFontStyle): React.CSSProperties {
  return {
    fontFamily: font.fontFamily,
    fontSize: font.fontSize,
    fontWeight: font.fontWeight,
    color: font.color,
    letterSpacing: font.letterSpacing,
    lineHeight: font.lineHeight,
    textTransform: font.textTransform,
    textAlign: font.textAlign ?? 'left',
    width: '100%',
  };
}
